import React, {useState} from 'react'
import {useParams} from 'react-router-dom'
import {useSolv, useTenant} from "../components/SolvProvider"
import {useIntlEx} from "../components/IntlUtils"
import {ActionButton, Alert, FormGroup, FormGroups, UrlInput} from "../components/FormComps.js"
import {clearAllErrors, InfoBlock, InfoText, validateField} from '../components/ValidationUtils.js';
import {default as Select} from "react-select";
import {AsyncPaginate, reduceGroupedOptions} from "react-select-async-paginate";
import getLogger from "../components/Logging.js"
import '../App.css'
import {mkExtraParams} from "../components/TableComps";
import {ModalDialog} from "../components/DialogUtils";
import {useTenantPlan} from "../components/AccountUtils";
import {isNonEmptyObject} from "../components/Utils";

const log = getLogger("SignupRequestApproveDialog")

export default function SignupRequestApproveDialog(props) {

  const {api, session: {user}, refreshSignin, setFatal, setBusy} = useSolv()
  const {intl} = useIntlEx()

  const params = useParams()

  const {tenant} = useTenant(params.tenantId)

  const {tenantPlanOpts, findTenantPlanOpt} = useTenantPlan("CLIENT", ["BUSINESS_A", "FUNDED", "DEMO"])

  const [signupRequestId, setSignupRequestId] = useState(null)

  const [touched, setTouched] = useState(false)
  const [alert, setAlert] = useState(null)

  const [signupRequest, setSignupRequest] = useState(null)
  const [fullName, setFullName] = useState(null)
  const [emailAddress, setEmailAddress] = useState(null)
  const [tenantName, setTenantName] = useState(null)
  const [homePage, setHomePage] = useState(null)
  const [reseller, setReseller] = useState(null)
  const [region, setRegion] = useState(null)
  const [currency, setCurrency] = useState(null)
  const [tenantPlan, setTenantPlan] = useState(null)
  const [step, setStep] = useState("START")

  function handleFullNameChange(e) {
    const v = e.target.value
    setFullName(v)
    setTouched(true)
  }

  function handleEmailAddressChange(e) {
    const v = e.target.value
    setEmailAddress(v)
    setTouched(true)
  }

  function handleTenantNameChange(e) {
    const v = e.target.value
    setTenantName(v)
    setTouched(true)
  }

  function handleResellerChange(v) {
    setReseller(v)
    setTouched(true)
  }

  function handleTenantPlanChange(v) {
    setTenantPlan(v)
    setTouched(true)
  }

  function handleRegionChange(v) {
    setRegion(v)
    setTouched(true)
  }

  function handleCurrencyChange(v) {
    setCurrency(v)
    setTouched(true)
  }

  function handleHomePageChange(e) {
    setHomePage(e.target.value)
    setTouched(true)
  }

  function validate() {

    let opt = {focusEl: null, valid: true}

    clearAllErrors("frmMain")

    validateField("inp_fullName", fullName && fullName.trim().length > 0, "required", opt)
    validateField("inp_emailAddress", emailAddress && emailAddress.trim().length > 0, "required", opt)
    validateField("inp_tenantName", tenantName && tenantName.trim().length > 0, "required", opt)
    validateField("inp_reseller", reseller && reseller.value, "required", opt)
    validateField("inp_region", region && region.value, "required", opt)
    validateField("inp_currency", currency && currency.value, "required", opt)

    if (opt.focusEl) {
      document.getElementById(opt.focusEl).focus()
    }

    return opt.valid

  }

  function loadResellers(search, loadedOptions, options) {
    return api.listResellers({
      tenantId: tenant.tenantId,
      extraParams: mkExtraParams({
        filterParams: {tenantName: search?.trim(), countryCodes: [signupRequest.countryCode]},
        sortParams: {tenantName: "ASC"}
      }),
      cursor: options?.cursor
    })
      .then(({data, nextCursor}) => {
        log.debug(">>>Z: listResellers: data=", data)
        let opt = data.filter(c => !["INVITING"].includes(c.status)).map(c => {
          return {
            value: c.resellerId,
            label: c.tenantName,
            tenantId: c.tenantId,
            owner: c.owner
          };
        });
        let res = {
          options: opt,
        }
        if (nextCursor) {
          res = {
            ...res,
            hasMore: true,
            additional: {
              cursor: nextCursor
            }
          }
        }
        return res
      });
  }

  function loadAssignableRegions(search, loadedOptions, options) {
    return api.listAssignableRegions({tenantId: reseller?.tenantId, extraParams: mkExtraParams({filterParams:{regionName: search}, sortParams:{regionName: "ASC"}}), cursor: options?.cursor})
      .then(({data, nextCursor}) => {
        let opt = data.map(c => {
          return ({value: c.regionId, label: c.regionName})
        });
        let res = {
          options: opt,
        }
        if (opt.length > 0) {
          setRegion(opt[0])
        }
        if (nextCursor) {
          res = {
            ...res,
            hasMore: true,
            additional: {
              cursor: nextCursor
            }
          }
        }
        return res
      })
  }


  function loadCurrencies(search, loadedOptions, cursor) {
    return api.listCurrencies({
      tenantId: user.tenantId,
      extraParams: mkExtraParams({
        filterParams: {displayName: search, paymentProviders: "PAYPAL\tSTRIPE"},
        sortParams: {displayName: "ASC"}
      }),
      cursor: cursor
    })
      .then(({data, nextCursor}) => {
        let opt = data.map(c => {
          return ({value: c.currencyCode, label: c.displayName})
        });
        let res = {
          options: opt,
        }
        if (nextCursor) {
          res = {
            ...res,
            hasMore: true,
            additional: {
              cursor: nextCursor
            }
          }
        }
        return res
      })
  }

  async function handleApproveClick() {

    log.debug("handleApproveClick: invoked" )

    if (signupRequest && validate()) {

      setBusy(intl.msg("saving"))

      try {

        let payload = {
          fullName: fullName,
          emailAddress: emailAddress,
          tenantName: tenantName,
          tenantPlanId: tenantPlan.value,
          resellerId: reseller.value,
          regionId: region.value,
          currencyCode: currency.value,
          homePage: homePage,
        }

        log.debug("approve: payload=", payload)

        await api.approveSignupRequest(signupRequest.signupRequestId, payload)

        window.location = `/sys/signupRequests`

      }
      catch (error) {
        handleError(error)
      }
      finally {
        setBusy(null)
      }
    }
    else {
      setAlert({type: "error", text: intl.msg("error_validation")})
    }

  }

  function handleError(error) {
    if (error && error.code) {
      // log.debug("handleError", error.code)
      const code = error.code
      const details = error.details
      switch (code) {
        case "BANNED_INVITER":
          setAlert({type: "error", text: intl.msg("error_banned_user", details)})
          break
        case "UNKNOWN_INVITER":
        case "INVALID_INVITER":
          setAlert({type: "error", text: intl.msg("invite_error_invalid_inviter", details), field: "inp_emailAddresses", constraint: "valid"})
          break
        case "DUPLICATE_INVITEE_USER":
          setAlert({type: "error", text: intl.msg("invite_error_duplicate_user", details), field: "inp_emailAddresses", constraint: "unique"})
          break
        case "DUPLICATE_INVITEE_TENANT":
          setAlert({type: "error", text: intl.msg("invite_error_duplicate_tenant", details), field: "inp_tenantName", constraint: "unique"})
          break
        case "BANNED_EMAIL_ADDRESS":
          setAlert({type: "error", text: intl.msg("error_banned_email_address", details), field: "inp_emailAddresses", constraint: "valid"})
          break
        default:
          setAlert({type: "error", text: intl.msg("error_failed", details)})
          break
      }
    }
    else {
      setAlert({type: "error", text: intl.msg("error_failed")})
    }
  }

  function isEditable() {
    return user.isSystem() && user.isManagerOrAbove()
  }

  function handleOpenDialog(...args) {
    log.debug(">>>Z: handleOpenDialog: args=", args)
    if (props?.signupRequestId) {
      setBusy(intl.msg("loading"))
      api.getSignupRequest(props.signupRequestId)
        .then(({data}) => {
          log.debug("handleOpenDialog: getSignupRequest: data=", data)
          setSignupRequest(data)
          setFullName(data.fullName)
          setEmailAddress(data.emailAddress)
          if (isNonEmptyObject(data?.etc?.request)) {
            if (data.etc.request.BIZ_NAME) {
              setTenantName(data.etc.request.BIZ_NAME)
            }
            if (data.etc.request.WEB_ADDRESS) {
              setHomePage(data.etc.request.WEB_ADDRESS)
            }
          }
        })
        .catch((error) => {
          log.error(error)
          setFatal(error)
        })
        .finally(() => {
          setBusy(null)
        })
    }
  }

  function handleCloseDialog() {
    window.location = `/sys/signupRequests/${props.signupRequestId}`
  }

  return (
    <>
      <ModalDialog
        dialogId={props.id || `dlg_signup_request_approve`}
        size={props.size || "md"}
        keyboard={"true"}
        backdrop={props.backdrop || "static"}
        onOpen={handleOpenDialog}
        onClose={handleCloseDialog}>

        <ModalDialog.Header
          title={intl.msg(`signup_request_approve_title`)}
          description={intl.msg(`signup_request_approve_subtitle`)}
          alert={alert}/>

        <ModalDialog.Body>

          {
            "APPROVE" === step ? (

              <Alert type="success" text={`Sign-up Request Approved! An invitation has been sent to applicant at ${signupRequest.emailAddress}`}/>

            ) : (

              <FormGroups>

                  <FormGroup>
                    <FormGroup.Label htmlFor="inp_tenantName" text={intl.msg("account_invite_owner_name")} description={intl.msg("account_invite_owner_name_description")}/>
                    <FormGroup.Controls>
                      <input id="inp_tenantName" type="text" className="form-control" value={fullName} onChange={handleFullNameChange}/>
                      <InfoBlock>
                        <InfoText validate="required">{intl.msg("helptext_required")}</InfoText>
                      </InfoBlock>
                    </FormGroup.Controls>
                  </FormGroup>

                  <FormGroup>
                    <FormGroup.Label htmlFor="inp_emailAddress" text={intl.msg("account_invite_email_address")} description={intl.msg("account_invite_email_address_description")}/>
                    <FormGroup.Controls>
                      <input  id="inp_emailAddress" className="form-control" type="email" value={emailAddress} autoFocus onChange={handleEmailAddressChange}/>
                      <InfoBlock>
                        <InfoText validate="valid">{intl.msg("helptext_email_address_valid")}</InfoText>
                        <InfoText validate="unique">{intl.msg("helptext_email_address_unique")}</InfoText>
                        <InfoText validate="required">{intl.msg("helptext_required")}</InfoText>
                      </InfoBlock>
                    </FormGroup.Controls>
                  </FormGroup>

                  <FormGroup>
                    <FormGroup.Label htmlFor="inp_tenantName" text={intl.msg("account_name")} description={intl.msg("account_invite_account_name_description")}/>
                    <FormGroup.Controls>
                      <input id="inp_tenantName" type="text" className="form-control" value={tenantName} onChange={handleTenantNameChange}/>
                      <InfoBlock>
                        <InfoText validate="unique">{intl.msg("account_name_helptext_unique")}</InfoText>
                        <InfoText validate="required">{intl.msg("helptext_required")}</InfoText>
                      </InfoBlock>
                    </FormGroup.Controls>
                  </FormGroup>

                  <FormGroup>
                    <FormGroup.Label text={intl.msg("account_plan", {tenantType: "CLIENT"})} description={intl.msg("account_invite_account_plan_description", {tenantType: "CLIENT"})} htmlFor="inp_tenantPlan"/>
                    <FormGroup.Control>
                      <Select id="inp_tenantPlan" className="react-select" classNamePrefix="react-select" value={tenantPlan} options={tenantPlanOpts} placeholder={null} /* hideSelectedOptions={true} */ isSearchable={false} onChange={handleTenantPlanChange}/>
                      <InfoBlock>
                        <InfoText validate="required">{intl.msg("helptext_required")}</InfoText>
                      </InfoBlock>
                    </FormGroup.Control>
                  </FormGroup>

                  <FormGroup>
                    <FormGroup.Label htmlFor="inp_reseller" text={intl.msg("reseller")} description={intl.msg("client_invite_reseller_description")}/>
                    <FormGroup.Controls>
                      <div style={{maxWidth: "600px"}}>
                        <AsyncPaginate id="inp_reseller" className="react-select" classNamePrefix="react-select" cacheOptions/* cacheUniqs={[country]} */ value={reseller} debounceTimeout={800} defaultOptions={true} placeholder={null} loadOptions={loadResellers} onChange={handleResellerChange}/></div>
                      <InfoBlock disabled={!isEditable()}>
                        <InfoText validate="required">{intl.msg("helptext_required")}</InfoText>
                      </InfoBlock>
                    </FormGroup.Controls>
                  </FormGroup>

                  <FormGroup hidden={!reseller}>
                    <FormGroup.Label text={intl.msg("region")} description={intl.msg("client_invite_region_description")} htmlFor="inp_region"/>
                    <FormGroup.Control>
                      <AsyncPaginate className="react-select" classNamePrefix="react-select" cacheOptions cacheUniqs={[reseller]} value={region} defaultOptions={true} loadOptions={loadAssignableRegions} reduceOptions={reduceGroupedOptions} hideSelectedOptions={true} placeholder="" onChange={handleRegionChange}/>
                      <InfoBlock>
                        <InfoText validate="required">{intl.msg("helptext_required")}</InfoText>
                      </InfoBlock>
                    </FormGroup.Control>
                  </FormGroup>

                  <FormGroup>
                    <FormGroup.Label htmlFor="inp_currency" text={intl.msg("currency")} description={intl.msg("client_invite_currency_description")}/>
                    <FormGroup.Controls>
                      <div style={{maxWidth: "600px"}}>
                        <AsyncPaginate id="inp_currency" className="react-select" classNamePrefix="react-select" cacheOptions value={currency} debounceTimeout={800} defaultOptions={true} placeholder={null} loadOptions={loadCurrencies} onChange={handleCurrencyChange}/>
                      </div>
                      <InfoBlock disabled={!isEditable()}>
                        <InfoText validate="required">{intl.msg("helptext_required")}</InfoText>
                      </InfoBlock>
                    </FormGroup.Controls>
                  </FormGroup>

                  <FormGroup>
                    <FormGroup.Label htmlFor="inp_homePage" text={intl.msg("account_home_page")} description={intl.msg("account_invite_home_page_description")}/>
                    <FormGroup.Controls>
                      <UrlInput id="inp_homePage" value={homePage} onChange={handleHomePageChange}/>
                      <InfoBlock>
                        <InfoText>{intl.msg("helptext_optional")}</InfoText>
                      </InfoBlock>
                    </FormGroup.Controls>
                  </FormGroup>

                </FormGroups>
            )
          }

        </ModalDialog.Body>

        <ModalDialog.Footer>
          <ModalDialog.Footer.Controls>

            {
              "APPROVED" === step ? (
                <>
                  <ActionButton name="ok" flavor="secondary" onClick={handleCloseDialog}/>
                </>
              ) : (
                <>
                  <ActionButton name="cancel" flavor="secondary" onClick={handleCloseDialog}/>
                  <ActionButton name="approve" flavor="primary" disabled={!touched} onClick={handleApproveClick}/>
                </>
              )
            }

          </ModalDialog.Footer.Controls>
        </ModalDialog.Footer>

      </ModalDialog>
    </>
  )
}

